import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { BibliographyEntry, CitationIndex, ResourceClass } from '../types/atlas';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PdfLightbox } from '../components/PdfLightbox';
import { ImageLightbox } from '../components/ImageLightbox';
import { ReferenceTooltip } from '../components/ReferenceTooltip';
import { atlasClient } from '../api/atlasClient';
import { isAtlasArticleType } from '../utils/citationPages';

// Copied from SparkArticleViewer
const REFERENCE_PATTERN = '\\[([A-Z0-9]{4}(?:-[A-Z0-9]+)?(?:,\\s*[A-Z0-9]{4}(?:-[A-Z0-9]+)?)*)\\]';

// Module-level LRU cache for resolved references (persists across hook instances/page navigations)
const RESOLVED_REFS_CACHE_SIZE = 500;
const globalResolvedRefsCache = new Map<string, BibliographyEntry | null>();

// Session storage-based cache for citation indexes (persists across navigations AND HMR reloads)
const CITATION_INDEX_CACHE_KEY = 'citation_index_cache';
const CITATION_INDEX_CACHE_SIZE = 20;
const CITATION_INDEX_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachedCitationIndex {
  index: CitationIndex;
  timestamp: number;
}

function getCitationIndexCacheMap(): Map<string, CachedCitationIndex> {
  try {
    const stored = sessionStorage.getItem(CITATION_INDEX_CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Map(Object.entries(parsed));
    }
  } catch (e) {
    console.warn('[Citation] Failed to parse session storage cache:', e);
  }
  return new Map();
}

function saveCitationIndexCacheMap(cache: Map<string, CachedCitationIndex>): void {
  try {
    const obj: Record<string, CachedCitationIndex> = {};
    cache.forEach((v, k) => { obj[k] = v; });
    sessionStorage.setItem(CITATION_INDEX_CACHE_KEY, JSON.stringify(obj));
  } catch (e) {
    console.warn('[Citation] Failed to save session storage cache:', e);
  }
}

function setCitationIndexCache(projectFqn: string, index: CitationIndex): void {
  const cache = getCitationIndexCacheMap();

  // Remove existing entry to update timestamp
  cache.delete(projectFqn);

  // If at capacity, remove oldest entry
  if (cache.size >= CITATION_INDEX_CACHE_SIZE) {
    let oldestKey: string | null = null;
    let oldestTime = Infinity;
    cache.forEach((entry, key) => {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    });
    if (oldestKey) cache.delete(oldestKey);
  }

  cache.set(projectFqn, { index, timestamp: Date.now() });
  saveCitationIndexCacheMap(cache);
}

function getCitationIndexCache(projectFqn: string): CitationIndex | undefined {
  const cache = getCitationIndexCacheMap();
  const entry = cache.get(projectFqn);
  if (entry) {
    // Check TTL - expire after 1 hour
    if (Date.now() - entry.timestamp > CITATION_INDEX_CACHE_TTL_MS) {
      cache.delete(projectFqn);
      saveCitationIndexCacheMap(cache);
      return undefined;
    }
    return entry.index;
  }
  return undefined;
}

function cacheSet(key: string, value: BibliographyEntry | null): void {
  // If key exists, delete it first to update its position
  if (globalResolvedRefsCache.has(key)) {
    globalResolvedRefsCache.delete(key);
  } else if (globalResolvedRefsCache.size >= RESOLVED_REFS_CACHE_SIZE) {
    // Delete the first (oldest) entry when at capacity
    const firstKey = globalResolvedRefsCache.keys().next().value;
    if (firstKey) globalResolvedRefsCache.delete(firstKey);
  }
  globalResolvedRefsCache.set(key, value);
}

function cacheGet(key: string): BibliographyEntry | null | undefined {
  const value = globalResolvedRefsCache.get(key);
  if (value !== undefined) {
    // Move to end (most recently used) by re-inserting
    globalResolvedRefsCache.delete(key);
    globalResolvedRefsCache.set(key, value);
  }
  return value;
}

function cacheHas(key: string): boolean {
  return globalResolvedRefsCache.has(key);
}

// Copied from SparkArticleViewer
const sortPageRefs = (resourceRef: string, pageRefs: string[], bibliography?: Record<string, BibliographyEntry>) => {
  return pageRefs.sort((a, b) => {
    const labelA = bibliography?.[resourceRef]?.details?.[a]?.label || '';
    const labelB = bibliography?.[resourceRef]?.details?.[b]?.label || '';
    const numA = parseInt(labelA, 10);
    const numB = parseInt(labelB, 10);
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
      return numA - numB;
    }
    return labelA.localeCompare(labelB);
  });
};

// Helper to derive resource class from type
export const getResourceClass = (type: string | undefined): ResourceClass => {
  if (!type) return 'PUBLIC'; // Default for unknown

  // INTERNAL: primary reports and all atlas types
  if (type === 'bedrock_primary_report' || type.startsWith('atlas_')) {
    return 'INTERNAL';
  }
  // SYNDICATED: secondary reports
  if (type === 'bedrock_secondary_report') {
    return 'SYNDICATED';
  }
  // PUBLIC: news and web articles (and default)
  return 'PUBLIC';
};

interface UseCitationReturn {
  markdownComponents: Components;
  markdownPlugins: any[];
  CitationPortal: React.FC;
  isLoading: boolean;
  error: string | null;
  refreshCitationIndex: () => void;
  handleReferenceClick: (ref: string, pageRef?: string) => Promise<void>;
}

interface UseCitationOptions {
  projectFqn?: string;
  bibliography?: Record<string, BibliographyEntry>;
  stripBracketRefs?: boolean; // When true, remove bracketed references entirely
  onNavigateToArticle?: (fqn: string, initialChapter?: string) => void; // Navigation callback for citation drill-down
}

export const useCitation = (options: UseCitationOptions | string): UseCitationReturn => {

  // Handle both old string signature and new options object for backward compatibility
  const { projectFqn, bibliography: providedBibliography, stripBracketRefs, onNavigateToArticle } = typeof options === 'string'
    ? { projectFqn: options, bibliography: undefined, stripBracketRefs: false, onNavigateToArticle: undefined }
    : options;

  const [citationIndex, setCitationIndex] = useState<CitationIndex | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [pdfLightbox, setPdfLightbox] = useState<{ url: string; page?: number } | null>(null);
  const [imageLightbox, setImageLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [referenceTooltip, setReferenceTooltip] = useState<{
    reference: string;
    entry: BibliographyEntry | null;
    pageRefs: string[];
    position: { x: number; y: number };
  } | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Pending resolution tracking (global cache is at module level)
  const pendingResolution = useRef<Set<string>>(new Set());
  const [dynamicRefsLoaded, setDynamicRefsLoaded] = useState(0); // Trigger re-render when refs resolved
  const [needsResolution, setNeedsResolution] = useState(0); // Trigger to check for unresolved refs

  // Fetch Citation Index on mount or when projectFqn changes OR refreshToken changes
  // Uses module-level cache to avoid re-fetching on navigation
  useEffect(() => {
    const abortController = new AbortController();

    const fetchIndex = async () => {
      // If bibliography is provided directly, use it instead of fetching
      if (providedBibliography) {
        setCitationIndex({
          bibliography: providedBibliography,
          tenant: '', // Not needed for article-only context
          refs_to_fqns: {} // Not needed for article-only context
        });
        setIsLoading(false);
        setError(null);
        return;
      }

      if (!projectFqn) {
        setCitationIndex(null);
        setIsLoading(false);
        setError(null); // Don't treat missing projectFqn as error when bibliography is provided
        return;
      }

      // Check session storage cache first (instant, no loading state)
      const cached = getCitationIndexCache(projectFqn);
      if (cached && refreshToken === 0) {
        // Use cached version immediately (skip loading state)
        setCitationIndex(cached);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const index = await atlasClient.getSparkCitationIndex(projectFqn);
        if (!abortController.signal.aborted) {
          setCitationIndex(index);
          // Cache for future navigations
          setCitationIndexCache(projectFqn, index);
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          return;
        }
        if (!abortController.signal.aborted) {
          console.error('Failed to fetch citation index:', err);
          setError(err instanceof Error ? err.message : 'Failed to load citations.');
          setCitationIndex(null);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchIndex();

    return () => {
      abortController.abort();
    };
  }, [projectFqn, refreshToken, providedBibliography]);

  // ADDED: function to refresh citation index
  const refreshCitationIndex = useCallback(() => {
    // Only refresh if we're using projectFqn-based fetching
    if (projectFqn && !providedBibliography) {
      setRefreshToken(prev => prev + 1);
    }
  }, [projectFqn, providedBibliography]);

  // Resolve unknown refs via bulk API (uses module-level LRU cache)
  const resolveUnknownRefs = useCallback(async (unknownRefs: string[]) => {
    // Filter and mark as pending atomically to prevent duplicate resolution
    const toResolve: string[] = [];
    for (const ref of unknownRefs) {
      if (!cacheHas(ref) && !pendingResolution.current.has(ref)) {
        pendingResolution.current.add(ref);
        toResolve.push(ref);
      }
    }

    if (toResolve.length === 0) return;

    try {
      const resolved = await atlasClient.resolveReferences(toResolve);
      // Store in global LRU cache
      Object.entries(resolved).forEach(([ref, entry]) => {
        cacheSet(ref, entry);
      });
      // Trigger re-render
      setDynamicRefsLoaded(prev => prev + 1);
    } catch (err) {
      console.error('Failed to resolve references:', err);
      // Mark as null (not found) to prevent retry
      toResolve.forEach(ref => {
        cacheSet(ref, null);
      });
    } finally {
      toResolve.forEach(ref => pendingResolution.current.delete(ref));
    }
  }, []);

  // Combined bibliography: project bibliography + dynamically resolved refs
  // 2-tier resolution:
  //   Tier 1: Project bibliography (has comments from source relevance)
  //   Tier 2: Dynamically resolved from global cache (no comments - just metadata)
  // Details are MERGED: bulk API has all pages, project bibliography has richer page info for cited pages
  const bibliography = useMemo(() => {
    const baseBibliography = citationIndex?.bibliography;
    // Filter out null entries from global resolved cache
    const resolvedEntries: Record<string, BibliographyEntry> = {};
    globalResolvedRefsCache.forEach((entry, ref) => {
      if (entry !== null) {
        resolvedEntries[ref] = entry;
      }
    });

    if (!baseBibliography && Object.keys(resolvedEntries).length === 0) {
      return undefined;
    }

    // Merge entries: base bibliography takes precedence for most fields,
    // but details are merged (bulk API has all pages, base has richer info for cited pages)
    const merged: Record<string, BibliographyEntry> = { ...resolvedEntries };
    if (baseBibliography) {
      for (const [ref, baseEntry] of Object.entries(baseBibliography)) {
        const resolvedEntry = resolvedEntries[ref];
        if (resolvedEntry) {
          // Merge: base entry wins for most fields, but merge details
          merged[ref] = {
            ...resolvedEntry,
            ...baseEntry,
            // Merge details: resolved has all pages, base has richer info for cited pages
            details: {
              ...resolvedEntry.details,
              ...baseEntry.details,
            },
          };
        } else {
          merged[ref] = baseEntry;
        }
      }
    }
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citationIndex?.bibliography, dynamicRefsLoaded]);

  // --- Copied Handlers from SparkArticleViewer (adapted slightly) ---

  const handleReferenceLeave = useCallback(() => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setTimeout(() => {
      const tooltipElement = document.querySelector('.reference-tooltip') as HTMLElement & { isMouseWithin?: boolean }; // Added type assertion
      if (tooltipElement && tooltipElement.isMouseWithin) {
        return;
      }
      setReferenceTooltip(null);
    }, 100);
  }, []);

  const handleReferenceHover = useCallback((ref: string, event: React.MouseEvent, pageRefs?: string[]) => {
    if (event.preventDefault) event.preventDefault();
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);

    // Capture target and rect immediately
    const targetElement = event.currentTarget as HTMLElement;
    if (!targetElement) return; // Exit if target is somehow null
    const rect = targetElement.getBoundingClientRect();

    hoverTimerRef.current = window.setTimeout(() => {
      const entry = bibliography?.[ref] || null;
      if (!entry) return; // Don't show tooltip if entry not found

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const estimatedWidth = 400; // Use actual width from CSS
      const estimatedHeight = 300; // Estimate

      // Prefer positioning below the element, but try above if not enough space
      let x = rect.left; 
      let y = rect.bottom + 5; // 5px below the element (relative to viewport)

      // Check if there's enough space below
      if (y + estimatedHeight > viewportHeight - 20) {
        // Try positioning above the element instead
        const yAbove = rect.top - estimatedHeight - 5;
        if (yAbove >= 20) {
          y = yAbove;
        } else {
          // If neither above nor below works well, position at bottom with adjustment
          y = Math.max(20, viewportHeight - estimatedHeight - 20);
        }
      }

      // Adjust horizontal position to stay within viewport bounds
      if (x + estimatedWidth > viewportWidth - 20) {
        x = Math.max(20, x - estimatedWidth - 20);
      }
      if (x < 20) x = 20;

      setReferenceTooltip({
        reference: ref,
        entry: entry,
        pageRefs: pageRefs || [],
        position: { x, y }
      });
      hoverTimerRef.current = null;
    }, 500);
  }, [bibliography]);

  const handleReferenceClick = useCallback(async (ref: string, pageRef?: string) => {
    setReferenceTooltip(null);
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    try {
      const resourceFqn = bibliography?.[ref]?.fqn;
      const resourceType = bibliography?.[ref]?.type;
      
      if (!resourceFqn) {
        console.warn(`Resource FQN not found in bibliography for ref: ${ref}`);
        return;
      }

      // Check if this is an Atlas article type that should navigate to viewer
      if (isAtlasArticleType(resourceType)) {
        if (onNavigateToArticle) {
          // Resolve chapter from pageRef if provided
          let initialChapter: string | undefined = undefined;
          if (pageRef && bibliography?.[ref]?.details?.[pageRef]) {
            const pageDetail = bibliography[ref]?.details?.[pageRef];
            const targetTitle = pageDetail?.label || pageDetail?.context;
            if (targetTitle) {
              // Fetch article to map chapter title to ID
              try {
                const article = await atlasClient.getProfileByFqn(resourceFqn);
                if (article.versions?.length > 0) {
                  const latestVersion = article.versions[article.versions.length - 1];
                  const chapters = Array.isArray(latestVersion.chapters)
                    ? latestVersion.chapters
                    : Object.values(latestVersion.chapters || {});
                  const matchingChapterIndex = chapters.findIndex((chapter: any) =>
                    chapter.title === targetTitle
                  );
                  if (matchingChapterIndex !== -1) {
                    const matchingChapter = chapters[matchingChapterIndex];
                    initialChapter = (matchingChapter as any).topic_fqn || `chapter-${matchingChapterIndex}`;
                  }
                }
              } catch (err) {
                // Chapter resolution failed, navigate without chapter
                console.warn('Failed to resolve chapter for navigation:', err);
              }
            }
          }
          onNavigateToArticle(resourceFqn, initialChapter);
          return;
        }
        // No navigation callback — fall through to PDF/URL fallback
      }

      // Existing PDF/URL logic for non-Atlas articles
      try {
        const blob = await atlasClient.downloadResourcePdf(resourceFqn);
        const url = window.URL.createObjectURL(blob);
        let page: number | undefined;
        if (pageRef) {
          const label = bibliography?.[ref]?.details?.[pageRef]?.label;
          if (label) {
            const pageNum = parseInt(label);
            if (!isNaN(pageNum)) page = pageNum;
          }
        }
        setPdfLightbox({ url, page });
      } catch (pdfError) {
        const resourceUrl = bibliography?.[ref]?.url;
        if (resourceUrl) {
          const scsMatch = resourceUrl.match(/^scs_(news|secondary):\/\/(.+)$/i);
          if (scsMatch) {
            const kind = scsMatch[1].toLowerCase() as 'news' | 'secondary';
            const itemId = scsMatch[2].trim();
            if (!itemId) {
              console.warn(`Invalid ContentStore url for resource: ${resourceFqn} (ref: ${ref})`);
              return;
            }
            try {
              const resolvedUrl = await atlasClient.resolveContentStoreItemUrl(itemId, kind);
              if (resolvedUrl) {
                window.open(resolvedUrl, '_blank');
              } else {
                console.warn(`Failed to resolve ContentStore url for resource: ${resourceFqn} (ref: ${ref})`);
              }
            } catch (resolveError) {
              console.error(`Failed to resolve ContentStore url for item ${itemId}:`, resolveError);
            }
          } else {
            window.open(resourceUrl, '_blank');
          }
        } else {
          console.warn(`No PDF or URL found for resource: ${resourceFqn} (ref: ${ref})`);
        }
      }
    } catch (error) {
      console.error('Error in handleReferenceClick:', error);
    }
  }, [bibliography, onNavigateToArticle]);

  // Track unknown refs found during processing - will trigger bulk resolution
  const unknownRefsToResolve = useRef<Set<string>>(new Set());
  const prevUnknownRefsSize = useRef(0);

  // --- Copied processReferences from SparkArticleViewer ---
  const processReferences = useCallback((content: string): React.ReactNode[] => {
      const parts: React.ReactNode[] = [];
      if (!content) return parts;

      let lastIndex = 0;
      let matches: { match: RegExpExecArray; index: number }[] = [];
      const regex = new RegExp(REFERENCE_PATTERN, 'g');
      let match: RegExpExecArray | null;

      while ((match = regex.exec(content)) !== null) {
        matches.push({ match, index: match.index });
      }

      const parseMatch = (m: RegExpExecArray) => {
        return m[1].split(',').map(ref => {
          const [resourceRef, pageRef] = ref.split('-').map(p => p.trim());
          return { resourceRef, pageRef };
        });
      };

      for (let i = 0; i < matches.length; i++) {
        const currentMatch = matches[i];
        if (currentMatch.index > lastIndex) {
          parts.push(content.slice(lastIndex, currentMatch.index));
        }

        let groupedCitations = parseMatch(currentMatch.match);
        let groupEnd = currentMatch.index + currentMatch.match[0].length;

        while (i + 1 < matches.length && content.slice(groupEnd, matches[i + 1].index).trim() === "") {
          i++;
          const nextMatch = matches[i];
          const nextCitations = parseMatch(nextMatch.match);
          nextCitations.forEach(({ resourceRef, pageRef }) => {
            const existing = groupedCitations.find(c => c.resourceRef === resourceRef);
            if (existing) {
              if (pageRef) groupedCitations.push({ resourceRef, pageRef });
            } else {
              groupedCitations.push({ resourceRef, pageRef });
            }
          });
          groupEnd = nextMatch.index + nextMatch.match[0].length;
        }

        if (stripBracketRefs) {
          // Skip adding any representation for the bracketed reference content
          lastIndex = groupEnd;
          continue;
        }

        const refGroups: Record<string, string[]> = {};
        groupedCitations.forEach(({ resourceRef, pageRef }) => {
          if (!refGroups[resourceRef]) refGroups[resourceRef] = [];
          if (pageRef) refGroups[resourceRef].push(pageRef);
          // Track unknown refs for bulk resolution (check both bibliography and global cache)
          if (!isLoading && !bibliography?.[resourceRef] && !cacheHas(resourceRef)) {
            unknownRefsToResolve.current.add(resourceRef);
          }
        });

        Object.entries(refGroups).forEach(([res, pages]) => {
          // Only sort if bibliography is available
          refGroups[res] = bibliography ? sortPageRefs(res, pages, bibliography) : pages;
        });

        parts.push(
          <span key={`ref-group-${currentMatch.index}`} className="reference-group">
            {Object.entries(refGroups).map(([res, pages], groupIndex) => {
              // Check resolution status - check both bibliography and global cache
              const isPending = pendingResolution.current.has(res) || unknownRefsToResolve.current.has(res);
              const cachedEntry = cacheGet(res);
              const wasNotFound = cachedEntry === null;
              const inBibliography = !!bibliography?.[res];
              const inCache = cachedEntry !== undefined && cachedEntry !== null;

              // Entry exists if in bibliography OR in cache
              const entryExists = inBibliography || inCache;

              // Get the entry from bibliography first, fall back to cache
              const entry = bibliography?.[res] || cachedEntry;

              // Missing if: not loading, not in bibliography, not in cache, not pending, and was explicitly not found
              const isMissing = !isLoading && !inBibliography && !inCache && !isPending && wasNotFound;

              // Resolving if: loading (waiting for citation index) OR pending bulk resolution
              // Only show as resolving if we don't already have the entry
              const isResolving = !entryExists && (isLoading || isPending);

              // Derive resource class for color-coding
              const resourceClass = getResourceClass(entry?.type);
              const spanClasses = `inline-reference inline-reference-${resourceClass.toLowerCase()} ${isMissing ? 'inline-reference-missing' : ''} ${isResolving ? 'inline-reference-resolving' : ''}`;
              const allowInteraction = entryExists && !isLoading;

              // Get display label: show "..." during resolution, title clipped at 25 chars, or fallback to reference code
              const title = entry?.title;
              const displayLabel = isResolving
                ? '...'
                : title
                  ? (title.length > 25 ? title.slice(0, 25) + '…' : title)
                  : res;

              return (
                <React.Fragment key={`ref-${currentMatch.index}-${groupIndex}`}>
                  {groupIndex > 0 && ' '}
                  <span
                    className={spanClasses}
                    onClick={allowInteraction ? (e) => {
                      e.preventDefault();
                      handleReferenceClick(res, pages.length > 0 ? pages[0] : undefined);
                    } : undefined}
                    onMouseEnter={allowInteraction ? (e) => {
                      handleReferenceHover(res, e, pages);
                    } : undefined}
                    onMouseLeave={handleReferenceLeave}
                    title={title || res}
                  >
                    {displayLabel}
                  </span>
                </React.Fragment>
              );
            })}
          </span>
        );
        lastIndex = groupEnd;
      }
      
      if (lastIndex < content.length) {
        parts.push(content.slice(lastIndex));
      }
      return parts;
    }, [bibliography, handleReferenceClick, handleReferenceHover, handleReferenceLeave, isLoading, stripBracketRefs]);

  // Effect to detect when new unknown refs are added and trigger resolution
  // Runs on every render to check refs (refs don't trigger re-renders)
  useEffect(() => {
    const currentSize = unknownRefsToResolve.current.size;
    if (currentSize > prevUnknownRefsSize.current) {
      prevUnknownRefsSize.current = currentSize;
      setNeedsResolution(prev => prev + 1);
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to trigger bulk resolution for unknown refs
  useEffect(() => {
    if (!isLoading && unknownRefsToResolve.current.size > 0) {
      const refsToResolve = Array.from(unknownRefsToResolve.current);
      unknownRefsToResolve.current.clear();
      prevUnknownRefsSize.current = 0;
      resolveUnknownRefs(refsToResolve);
    }
  }, [isLoading, resolveUnknownRefs, needsResolution]);

  // --- Copied Markdown Components from SparkArticleViewer --- 
  const markdownComponents: Components = useMemo(() => ({
    li: ({ children, ...props }) => {
      const processedChildren: React.ReactNode[] = [];
      let buffer = "";
      React.Children.forEach(children, child => {
        if (typeof child === "string") {
          buffer += child;
        } else {
          if (buffer) {
            processedChildren.push(...processReferences(buffer));
            buffer = "";
          }
          processedChildren.push(child);
        }
      });
      if (buffer) {
        processedChildren.push(...processReferences(buffer));
      }
      return <li {...props}>{processedChildren}</li>;
    },
    p: ({ children, ...props }) => {
      const processedChildren: React.ReactNode[] = [];
      let buffer = "";
      React.Children.forEach(children, child => {
        if (typeof child === "string") {
          buffer += child;
        } else {
          if (buffer) {
            processedChildren.push(...processReferences(buffer));
            buffer = "";
          }
          processedChildren.push(child);
        }
      });
      if (buffer) {
        processedChildren.push(...processReferences(buffer));
      }
      return <p {...props}>{processedChildren}</p>;
    },
    br: () => <br />,
    text: ({ children }) => {
      return <>{processReferences(String(children))}</>;
    },
    img: ({ node, src, alt = '', title, ...props }) => {
      if (!src) return null;
      
      let displaySrc = src as string;
      let fullSrc = src as string;
      if (typeof displaySrc === 'string' && displaySrc.startsWith('asset:')) {
        const id = displaySrc.replace('asset:', '');
        fullSrc = atlasClient.getAssetUrl(id, { full: true });
        displaySrc = atlasClient.getAssetUrl(id);
      }

      const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
        e.preventDefault();
        setImageLightbox({ 
          src: fullSrc as string, 
          alt: alt || (title as string) || '' 
        });
      };

      return (
        <img 
          {...props}
          src={displaySrc as string} 
          alt={alt} 
          title={title} 
          onClick={handleImageClick}
          style={{ cursor: 'pointer', maxWidth: '100%', height: 'auto' }} 
        />
      );
    },
    table: ({ children, ...props }) => (
      <div className="table-container">
        <table {...props}>{children}</table>
      </div>
    ),
    th: ({ children, ...props }) => {
      const processedChildren: React.ReactNode[] = [];
      let buffer = "";
      React.Children.forEach(children, child => {
        if (typeof child === "string") {
          buffer += child;
        } else {
          if (buffer) {
            processedChildren.push(...processReferences(buffer));
            buffer = "";
          }
          processedChildren.push(child);
        }
      });
      if (buffer) {
        processedChildren.push(...processReferences(buffer));
      }
      return <th {...props} className="table-header">{processedChildren}</th>;
    },
    td: ({ children, ...props }) => {
      const processedChildren: React.ReactNode[] = [];
      let buffer = "";
      React.Children.forEach(children, child => {
        if (typeof child === "string") {
          buffer += child;
        } else {
          if (buffer) {
            processedChildren.push(...processReferences(buffer));
            buffer = "";
          }
          processedChildren.push(child);
        }
      });
      if (buffer) {
        processedChildren.push(...processReferences(buffer));
      }
      return <td {...props}>{processedChildren}</td>;
    },
  }), [processReferences]);

  // Component to render the lightboxes and tooltip
  const CitationPortal: React.FC = useCallback(() => (
    <>
      {pdfLightbox && (
        <PdfLightbox
          url={pdfLightbox.url}
          initialPage={pdfLightbox.page}
          onClose={() => setPdfLightbox(null)}
        />
      )}
      {imageLightbox && (
        <ImageLightbox
          src={imageLightbox.src}
          alt={imageLightbox.alt}
          onClose={() => setImageLightbox(null)}
        />
      )}
      {referenceTooltip && referenceTooltip.entry && (
        <ReferenceTooltip
          reference={referenceTooltip.reference}
          entry={referenceTooltip.entry}
          pageRefs={referenceTooltip.pageRefs}
          position={referenceTooltip.position}
          resourceClass={getResourceClass(referenceTooltip.entry?.type)}
          onClose={() => setReferenceTooltip(null)}
          onOpenPdf={(pageRef?: string) => {
            handleReferenceClick(referenceTooltip.reference, pageRef);
          }}
        />
      )}
    </>
  ), [pdfLightbox, imageLightbox, referenceTooltip, handleReferenceClick]);

  return {
    markdownComponents,
    markdownPlugins: [remarkGfm],
    CitationPortal,
    isLoading,
    error,
    refreshCitationIndex,
    handleReferenceClick,
  };
}; 
